import { Box, Link, Palette, Typography } from '@mui/material';
import type { JSXConvertersFunction } from '@payloadcms/richtext-lexical/react';
import OrderedList from '../components/OrderedList';
import Divider from '../components/Divider';
import BlockImage from '../components/BlockImage';
import BlockVideo from '../components/BlockVideo';
import CtaPanel from '../components/CtaPanel';
import Callout from '../components/Callout';
import TwoColumn from '../components/TwoColumn';
import ButtonLink from '../components/ButtonLink';
import TextNode from './text';
import BreadcrumbBar from '../../breadcrumbs';
import Tags from '../../tags';
import {
  hrefOnlyLinkMapper,
  LinkMapperInputType,
} from '../../../utils/mappers/link';
import Accordion from '../../sections/accordion';
import Timeline from '../../sections/timeline';
import { TenantConfig } from '@jd/client/tenant/getSiteConfig';

/** Link node fields are untyped JSON, so the extra link fields are narrowed here. */
const toSectionId = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

const toQueryParams = (value: unknown): LinkMapperInputType['queryParams'] =>
  Array.isArray(value)
    ? value.flatMap((item) =>
        item &&
        typeof item === 'object' &&
        typeof item.key === 'string' &&
        typeof item.value === 'string'
          ? [{ key: item.key, value: item.value }]
          : [],
      )
    : undefined;

const getConverters =
  (palette: Palette, config: TenantConfig): JSXConvertersFunction =>
  ({ defaultConverters }) => {
    const blogHubSlug = config.other?.blogHubSlug ?? 'blog';
    return {
      ...defaultConverters,
      heading: ({ node, nodesToJSX }) => {
        const { tag } = node;
        const pbMap = {
          h1: 3,
          h2: 3,
          h3: 2,
          h4: 2,
          h5: 1.5,
          h6: 1.5,
        };
        const mtMap = {
          h1: { md: 8, xs: 4 },
          h2: { md: 8, xs: 4 },
          h3: { md: 4, xs: 2 },
          h4: { md: 2, xs: 1 },
          h5: 1,
          h6: 1,
        };

        return (
          <Typography
            variant={tag}
            sx={{ pb: pbMap[tag] || 2, mt: mtMap[tag], textAlign: node.format }}
          >
            {nodesToJSX({ nodes: node.children })}
          </Typography>
        );
      },
      paragraph: ({ node, nodesToJSX }) => (
        <Typography variant="body1" sx={{ pb: 1, textAlign: node.format }}>
          {nodesToJSX({ nodes: node.children })}
        </Typography>
      ),
      quote: ({ node, nodesToJSX }) => (
        <Box
          component="blockquote"
          sx={{
            borderLeft: `4px solid ${palette.grey[400]}`,
            pl: 2,
            ml: 0,
            my: 2,
            fontStyle: 'italic',
            textAlign: node.format,
          }}
        >
          <Typography variant="body1">
            {nodesToJSX({ nodes: node.children })}
          </Typography>
        </Box>
      ),
      list: ({ node, nodesToJSX }) => {
        if (node.listType === 'number') {
          return (
            <OrderedList>{nodesToJSX({ nodes: node.children })}</OrderedList>
          );
        }
        return (
          // @ts-ignore
          <ul style={{ textAlign: node.format }}>
            {nodesToJSX({ nodes: node.children })}
          </ul>
        );
      },
      listitem: ({ node, nodesToJSX }) => (
        <li>{nodesToJSX({ nodes: node.children })}</li>
      ),
      link: ({ node, nodesToJSX }) => {
        const { url, newTab, doc, linkType, sectionId, queryParams } =
          node.fields || {};
        const href = hrefOnlyLinkMapper(
          {
            type: linkType === 'internal' ? 'reference' : 'custom',
            url,
            reference:
              typeof doc === 'object' && doc
                ? (doc as LinkMapperInputType['reference'])
                : undefined,
            sectionId: toSectionId(sectionId),
            queryParams: toQueryParams(queryParams),
          },
          { blogHubSlug },
        );

        return (
          <Link
            href={href}
            target={newTab ? '_blank' : undefined}
            sx={{ textDecoration: 'underline' }}
          >
            {nodesToJSX({ nodes: node.children })}
          </Link>
        );
      },
      horizontalrule: () => {
        return <Divider value={'line'} />;
      },
      blocks: {
        ...(defaultConverters.blocks || {}),
        blockImage: ({ node }: any) => <BlockImage {...node.fields} />,
        blockVideo: ({ node }: any) => <BlockVideo {...node.fields} />,
        buttonLink: ({ node }: any) => <ButtonLink {...node.fields} />,
        ctaPanel: ({ node }: any) => <CtaPanel {...node.fields} />,
        callout: ({ node }: any) => <Callout {...node.fields} />,
        twoColumn: ({ node }: any) => <TwoColumn {...node.fields} />,
        breadcrumbs: ({ node }: any) => (
          <BreadcrumbBar
            title={node.fields.title}
            crumbs={node.fields.crumbs}
          />
        ),
        accordionRichText: ({ node }: any) => (
          <Accordion
            section={node.fields}
            context={{
              config,
              index: 0,
              isEven: true,
              attrData: {
                id: `accordionRichText-1`,
                type: 'accordionRichText',
              },
            }}
            isMobile={false}
          />
        ),
        timelineRichText: ({ node }: any) => (
          <Timeline
            section={{
              ...node.fields,
              styling: {
                ...node.fields.style, // todo: deprecated - remove after 20/06/2026
                ...node.fields.styling,
                titlesStyle: { containerSx: { px: 0 } },
              },
            }}
            context={{
              config,
              index: 0,
              isEven: true,
              attrData: {
                id: `timelineRichText-1`,
                type: 'timelineRichText',
              },
            }}
            isMobile={false}
          />
        ),
        tags: ({ node }: any) => <Tags tags={node.fields.tags} />,
        spacing: ({ node }: any) => (
          <Divider value={'space'} {...node.fields} />
        ),
      },
      text: ({ node }) => <TextNode node={node} palette={palette} />,
    };
  };

export default getConverters;
